/**
 * Toast Test Page
 * Test page to demonstrate different toast notification types
 * 
 * @author Varun Pandey
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { Logo } from '../../components/ui/Logo';

// Toast will be initialized in the component
let toast: any = null;
let isToastAvailable = false;

export const ToastTestPage: React.FC = () => {
  const navigate = useNavigate();
  const [toastReady, setToastReady] = React.useState(false);

  // Initialize toast on mount
  React.useEffect(() => {
    const initToast = async () => {
      try {
        const toastModule = await import('react-hot-toast');
        (toast as any) = toastModule.default;
        (isToastAvailable as any) = true;
        setToastReady(true);
      } catch (e) {
        // Package not installed - use mock
        const mockToast = (message: string | React.ReactNode, _options?: any) => {
          console.log('📢 Toast:', message);
          const msg = typeof message === 'string' ? message : 'Custom toast message';
          alert(`${msg}\n\nNote: Install react-hot-toast to see actual toast notifications.`);
          return 'mock-toast-id';
        };

        mockToast.success = (msg: string) => {
          console.log('✅ Toast Success:', msg);
          alert(`Success: ${msg}\n\nNote: Install react-hot-toast to see actual toast notifications.`);
          return 'mock-toast-id';
        };

        mockToast.error = (msg: string) => {
          console.error('❌ Toast Error:', msg);
          alert(`Error: ${msg}\n\nNote: Install react-hot-toast to see actual toast notifications.`);
          return 'mock-toast-id';
        };

        mockToast.loading = (msg: string) => {
          console.log('⏳ Toast Loading:', msg);
          return 'mock-toast-id';
        };

        mockToast.promise = (promise: Promise<any>, messages: any) => {
          console.log('⏳ Toast Promise:', messages.loading);
          promise
            .then(() => console.log('✅', messages.success))
            .catch(() => console.error('❌', messages.error));
          return promise;
        };

        mockToast.dismiss = () => { };

        (toast as any) = mockToast;
        setToastReady(true);
      }
    };
    initToast();
  }, []);

  const showSuccessToast = () => {
    if (!toast) return;
    toast.success('Operation completed successfully!');
  };

  const showErrorToast = () => {
    if (!toast) return;
    toast.error('Something went wrong. Please try again.');
  };

  const showInfoToast = () => {
    if (!toast) return;
    toast('This is an informational message', {
      icon: 'ℹ️',
    });
  };

  const showLoadingToast = () => {
    if (!toast) return;
    const toastId = toast.loading('Processing your request...');
    // Simulate async operation
    setTimeout(() => {
      if (toast) {
        toast.success('Request completed!', { id: toastId });
      }
    }, 2000);
  };

  const showPromiseToast = () => {
    if (!toast) return;
    const myPromise = new Promise((resolve, reject) => {
      setTimeout(() => {
        Math.random() > 0.5 ? resolve('Data loaded!') : reject('Failed to load data');
      }, 2000);
    });

    toast.promise(myPromise, {
      loading: 'Loading data...',
      success: 'Data loaded successfully!',
      error: 'Failed to load data',
    });
  };

  const showCustomToast = () => {
    if (!toast) return;
    toast(
      (t: any) => (
        <div className="flex items-center gap-3">
          <span>Custom toast with action</span>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="rounded bg-[#F33363] px-3 py-1 text-xs font-medium text-white hover:bg-[#D92D58]"
          >
            Dismiss
          </button>
        </div>
      ),
      {
        duration: 5000,
      }
    );
  };

  const showLongToast = () => {
    if (!toast) return;
    toast('This is a longer toast message that contains more information and will stay visible for a longer duration to test how it looks with extended text content.', {
      duration: 5000,
    });
  };

  const showTopLeftToast = () => {
    if (!toast) return;
    toast.success('Top left position', {
      position: 'top-left',
    });
  };

  const showTopRightToast = () => {
    if (!toast) return;
    toast.success('Top right position', {
      position: 'top-right',
    });
  };

  const showBottomLeftToast = () => {
    if (!toast) return;
    toast.success('Bottom left position', {
      position: 'bottom-left',
    });
  };

  const showBottomRightToast = () => {
    if (!toast) return;
    toast.success('Bottom right position', {
      position: 'bottom-right',
    });
  };

  const showBottomCenterToast = () => {
    if (!toast) return;
    toast.success('Bottom center position', {
      position: 'bottom-center',
    });
  };

  return (
    <AuthLayout showImage={false}>
      <div className="space-y-6 px-4 py-8 lg:px-0">
        <Logo size="md" className="mb-4" />
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Toast Test Page</h1>
          <Button variant="active" onClick={() => navigate('/')} className="text-sm">
            Back
          </Button>
        </div>
        <p className="text-sm text-[#8E8E93]">
          Test different types of toast notifications. Make sure react-hot-toast is installed.
        </p>

        <div className="space-y-4">
          <div>
            <h2 className="mb-3 text-lg font-semibold text-white">Basic Toast Types</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Button variant="active" onClick={showSuccessToast} className="w-full">
                Success Toast
              </Button>
              <Button variant="active" onClick={showErrorToast} className="w-full">
                Error Toast
              </Button>
              <Button variant="active" onClick={showInfoToast} className="w-full">
                Info Toast
              </Button>
              <Button variant="active" onClick={showLoadingToast} className="w-full">
                Loading Toast
              </Button>
            </div>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-white">Advanced Toasts</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Button variant="active" onClick={showPromiseToast} className="w-full">
                Promise Toast
              </Button>
              <Button variant="active" onClick={showCustomToast} className="w-full">
                Custom Toast
              </Button>
              <Button variant="active" onClick={showLongToast} className="w-full">
                Long Message Toast
              </Button>
            </div>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-white">Toast Positions</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Button variant="active" onClick={showTopLeftToast} className="w-full text-xs">
                Top Left
              </Button>
              <Button variant="active" onClick={showTopRightToast} className="w-full text-xs">
                Top Right
              </Button>
              <Button variant="active" onClick={showBottomLeftToast} className="w-full text-xs">
                Bottom Left
              </Button>
              <Button variant="active" onClick={showBottomRightToast} className="w-full text-xs">
                Bottom Right
              </Button>
              <Button variant="active" onClick={showBottomCenterToast} className="w-full text-xs">
                Bottom Center
              </Button>
            </div>
          </div>

          {!toastReady && (
            <div className="rounded-lg border-2 border-[#F33363] bg-[#2C2C2E] p-4">
              <h3 className="mb-2 text-sm font-semibold text-[#F33363]">⏳ Loading Toast Library...</h3>
            </div>
          )}
          {toastReady && !isToastAvailable && (
            <div className="rounded-lg border-2 border-[#F33363] bg-[#2C2C2E] p-4">
              <h3 className="mb-2 text-sm font-semibold text-[#F33363]">⚠️ Toast Library Not Installed</h3>
              <p className="mb-2 text-xs text-[#8E8E93]">
                To see actual toast notifications, install react-hot-toast:
              </p>
              <code className="block rounded bg-[#0B0B0D] px-2 py-1.5 text-xs text-[#F33363]">
                npm install react-hot-toast
              </code>
              <p className="mt-2 text-xs text-[#8E8E93]">
                The Toaster component in <code className="rounded bg-[#0B0B0D] px-1 py-0.5 text-[#F33363]">src/components/ui/Toaster.tsx</code> will automatically work once installed.
                <br />
                Currently showing alerts as fallback.
              </p>
            </div>
          )}
          {toastReady && isToastAvailable && (
            <div className="rounded-lg bg-[#10B981]/10 border border-[#10B981] p-4">
              <h3 className="mb-2 text-sm font-semibold text-[#10B981]">✅ Toast Library Ready</h3>
              <p className="text-xs text-[#8E8E93]">
                react-hot-toast is installed and working! Click the buttons above to test different toast types.
              </p>
            </div>
          )}
        </div>
      </div>
    </AuthLayout>
  );
};
